(() => {
  const ROOT_SELECTOR = '[data-pft-v5-component]';
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  class PremiumFeatureTabsV5 {
    constructor(root) {
      this.root = root;
      this.tabs = Array.from(root.querySelectorAll('[data-pft-tab]'));
      this.panels = Array.from(root.querySelectorAll('[data-pft-panel]'));
      this.viewport = root.querySelector('[data-pft-viewport]');
      this.track = root.querySelector('[data-pft-track]');
      this.prevButton = root.querySelector('[data-pft-prev]');
      this.nextButton = root.querySelector('[data-pft-next]');
      this.reviewTimers = new Map();
      this.destroyed = false;
      this.mobileMedia = window.matchMedia('(max-width: 749px)');
      this.currentIndex = Math.max(0, this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'));
      this.transitionTimer = null;
      this.resizeFrame = null;
      this.swipe = this.createSwipeState();

      if (!this.tabs.length || !this.panels.length || !this.viewport || !this.track) return;

      this.onClick = this.handleClick.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onPointerDown = this.handlePointerDown.bind(this);
      this.onPointerMove = this.handlePointerMove.bind(this);
      this.onPointerUp = this.handlePointerUp.bind(this);
      this.onPointerCancel = this.handlePointerCancel.bind(this);
      this.onResize = this.handleResize.bind(this);
      this.onDragStart = (event) => {
        if (this.mobileMedia.matches && event.target.closest('[data-pft-panel]')) event.preventDefault();
      };

      this.root.addEventListener('click', this.onClick);
      this.root.addEventListener('keydown', this.onKeydown);
      this.viewport.addEventListener('pointerdown', this.onPointerDown, { passive: true });
      this.viewport.addEventListener('pointermove', this.onPointerMove, { passive: false });
      this.viewport.addEventListener('pointerup', this.onPointerUp, { passive: true });
      this.viewport.addEventListener('pointercancel', this.onPointerCancel, { passive: true });
      this.viewport.addEventListener('dragstart', this.onDragStart);
      window.addEventListener('resize', this.onResize, { passive: true });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.viewport);
        // Cubitt: también las pestañas, para recalcular la altura cuando cargan sus imágenes.
        this.panels.forEach((panel) => this.resizeObserver.observe(panel));
      }

      this.panels.forEach((panel) => {
        panel.hidden = false;
      });

      this.root.classList.add('is-js-ready');
      this.root.dataset.pftInitialized = 'v5.8-figma';
      this.updateState(this.currentIndex, { scrollTab: false, restartAnimation: true, initial: true });
      this.setTrackPosition(this.currentIndex, false);
      this.syncViewportHeight(this.currentIndex, false);
    }

    createSwipeState() {
      return {
        active: false,
        pointerId: null,
        startX: 0,
        startY: 0,
        lastX: 0,
        lastTime: 0,
        velocityX: 0,
        axis: null,
        rawDx: 0
      };
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      this.root.removeEventListener('click', this.onClick);
      this.root.removeEventListener('keydown', this.onKeydown);
      this.viewport.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport.removeEventListener('pointermove', this.onPointerMove);
      this.viewport.removeEventListener('pointerup', this.onPointerUp);
      this.viewport.removeEventListener('pointercancel', this.onPointerCancel);
      this.viewport.removeEventListener('dragstart', this.onDragStart);
      window.removeEventListener('resize', this.onResize);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      if (this.transitionTimer) window.clearTimeout(this.transitionTimer);
      this.reviewTimers.forEach((timer) => window.clearInterval(timer));
      this.reviewTimers.clear();
      this.root.classList.remove('is-js-ready', 'is-dragging');
      delete this.root.dataset.pftInitialized;
    }

    isSwipeBlockedTarget(target) {
      if (!target || !target.closest) return false;
      return Boolean(target.closest('a, button, input, textarea, select, summary, [contenteditable="true"], [data-pft-review-dot]'));
    }

    getSlideWidth() {
      return Math.max(1, this.viewport.clientWidth);
    }

    clampIndex(index) {
      return Math.max(0, Math.min(this.panels.length - 1, index));
    }

    handleResize() {
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        if (this.swipe.active) return;
        this.setTrackPosition(this.currentIndex, false);
        this.syncViewportHeight(this.currentIndex, false);
      });
    }

    handlePointerDown(event) {
      if (!this.mobileMedia.matches || event.pointerType === 'mouse' || this.isSwipeBlockedTarget(event.target)) return;
      const panel = event.target.closest('[data-pft-panel]');
      if (!panel || !this.root.contains(panel)) return;

      this.swipe = this.createSwipeState();
      this.swipe.active = true;
      this.swipe.pointerId = event.pointerId;
      this.swipe.startX = event.clientX;
      this.swipe.startY = event.clientY;
      this.swipe.lastX = event.clientX;
      this.swipe.lastTime = performance.now();
      this.track.classList.add('is-touch-ready');
    }

    handlePointerMove(event) {
      if (!this.swipe.active || event.pointerId !== this.swipe.pointerId || !this.mobileMedia.matches) return;

      const dx = event.clientX - this.swipe.startX;
      const dy = event.clientY - this.swipe.startY;

      if (this.swipe.axis === null) {
        if (Math.abs(dx) < 7 && Math.abs(dy) < 7) return;
        if (Math.abs(dy) > Math.abs(dx) * 1.08) {
          this.swipe.axis = 'y';
          this.endSwipeState();
          return;
        }
        this.swipe.axis = 'x';
        this.track.classList.add('is-dragging');
        this.root.classList.add('is-dragging');
        try {
          this.viewport.setPointerCapture(event.pointerId);
        } catch (error) {
          // Pointer capture is an enhancement only.
        }
      }

      if (this.swipe.axis !== 'x') return;
      event.preventDefault();

      const now = performance.now();
      const elapsed = Math.max(1, now - this.swipe.lastTime);
      const instantVelocity = (event.clientX - this.swipe.lastX) / elapsed;
      this.swipe.velocityX = (this.swipe.velocityX * 0.72) + (instantVelocity * 0.28);
      this.swipe.lastX = event.clientX;
      this.swipe.lastTime = now;
      this.swipe.rawDx = dx;

      const atStart = this.currentIndex === 0 && dx > 0;
      const atEnd = this.currentIndex === this.panels.length - 1 && dx < 0;
      const dragDx = atStart || atEnd ? this.applyEdgeResistance(dx) : dx;
      this.setTrackPosition(this.currentIndex, false, dragDx);
    }

    handlePointerUp(event) {
      if (!this.swipe.active || event.pointerId !== this.swipe.pointerId) return;
      this.finishSwipe(event.clientX, event.clientY);
    }

    handlePointerCancel(event) {
      if (!this.swipe.active || event.pointerId !== this.swipe.pointerId) return;
      this.snapBack();
    }

    applyEdgeResistance(dx) {
      const width = this.getSlideWidth();
      const sign = dx < 0 ? -1 : 1;
      const distance = Math.abs(dx);
      const resisted = width * 0.18 * (1 - Math.exp(-distance / (width * 0.23)));
      return sign * resisted;
    }

    finishSwipe(endX, endY) {
      const dx = endX - this.swipe.startX;
      const dy = endY - this.swipe.startY;

      if (this.swipe.axis !== 'x' || Math.abs(dx) <= Math.abs(dy)) {
        this.snapBack();
        return;
      }

      const width = this.getSlideWidth();
      const velocity = this.swipe.velocityX;
      const projectedDx = dx + (velocity * 135);
      const distanceTrigger = width * 0.18;
      const velocityTrigger = 0.34;
      let nextIndex = this.currentIndex;

      if (projectedDx <= -distanceTrigger || (velocity <= -velocityTrigger && dx < -18)) {
        nextIndex += 1;
      } else if (projectedDx >= distanceTrigger || (velocity >= velocityTrigger && dx > 18)) {
        nextIndex -= 1;
      }

      nextIndex = this.clampIndex(nextIndex);
      this.releasePointer();

      if (nextIndex === this.currentIndex) {
        this.snapBack();
        return;
      }

      const direction = nextIndex > this.currentIndex ? 'next' : 'prev';
      const speed = Math.min(1.6, Math.abs(velocity));
      const duration = REDUCED_MOTION.matches ? 0 : Math.round(Math.max(260, 430 - (speed * 95)));
      this.goToIndex(nextIndex, {
        scrollTab: true,
        animate: true,
        direction,
        duration,
        fromSwipe: true
      });
      this.endSwipeState();
    }

    snapBack() {
      this.releasePointer();
      this.track.classList.remove('is-dragging');
      this.root.classList.remove('is-dragging');
      this.setTrackPosition(this.currentIndex, !REDUCED_MOTION.matches, 0, 280);
      this.endSwipeState();
    }

    releasePointer() {
      if (!this.swipe.pointerId) return;
      try {
        if (this.viewport.hasPointerCapture(this.swipe.pointerId)) {
          this.viewport.releasePointerCapture(this.swipe.pointerId);
        }
      } catch (error) {
        // Nothing to release.
      }
    }

    endSwipeState() {
      this.track.classList.remove('is-touch-ready', 'is-dragging');
      this.root.classList.remove('is-dragging');
      this.swipe = this.createSwipeState();
    }

    handleClick(event) {
      const prev = event.target.closest('[data-pft-prev]');
      if (prev && this.root.contains(prev)) {
        this.goToIndex(this.currentIndex - 1, { scrollTab: true, animate: true, direction: 'prev' });
        return;
      }

      const next = event.target.closest('[data-pft-next]');
      if (next && this.root.contains(next)) {
        this.goToIndex(this.currentIndex + 1, { scrollTab: true, animate: true, direction: 'next' });
        return;
      }

      const tab = event.target.closest('[data-pft-tab]');
      if (tab && this.root.contains(tab)) {
        const index = this.tabs.indexOf(tab);
        if (index >= 0) {
          const direction = index > this.currentIndex ? 'next' : index < this.currentIndex ? 'prev' : null;
          this.goToIndex(index, { scrollTab: true, animate: index !== this.currentIndex, direction });
        }
        return;
      }

      const dot = event.target.closest('[data-pft-review-dot]');
      if (dot && this.root.contains(dot)) {
        const reviews = dot.closest('[data-pft-reviews]');
        if (reviews) this.activateReview(reviews, dot.dataset.reviewIndex, true);
      }
    }

    handleKeydown(event) {
      const current = event.target.closest('[data-pft-tab]');
      if (!current || !this.root.contains(current)) return;
      const index = this.tabs.indexOf(current);
      if (index < 0) return;

      let nextIndex = null;
      if (event.key === 'ArrowRight') nextIndex = (index + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.tabs.length - 1;
      if (nextIndex === null) return;

      event.preventDefault();
      const direction = nextIndex > this.currentIndex ? 'next' : 'prev';
      this.goToIndex(nextIndex, { scrollTab: true, animate: true, direction });
      this.tabs[nextIndex].focus();
    }

    activateByBlockId(blockId) {
      const index = this.tabs.findIndex((item) => item.dataset.blockId === blockId);
      if (index < 0) return;
      const direction = index > this.currentIndex ? 'next' : index < this.currentIndex ? 'prev' : null;
      this.goToIndex(index, { scrollTab: false, animate: !REDUCED_MOTION.matches, direction });
    }

    goToIndex(index, options = {}) {
      const targetIndex = this.clampIndex(index);
      const changed = targetIndex !== this.currentIndex;
      const direction = options.direction || (targetIndex > this.currentIndex ? 'next' : targetIndex < this.currentIndex ? 'prev' : null);
      const duration = typeof options.duration === 'number' ? options.duration : (REDUCED_MOTION.matches ? 0 : 420);

      if (this.transitionTimer) window.clearTimeout(this.transitionTimer);
      this.track.classList.remove('is-dragging', 'is-touch-ready');
      this.root.classList.remove('is-dragging');

      this.currentIndex = targetIndex;
      this.updateState(targetIndex, {
        scrollTab: options.scrollTab !== false,
        restartAnimation: changed,
        direction
      });
      this.syncViewportHeight(targetIndex, options.animate !== false);
      this.setTrackPosition(targetIndex, options.animate !== false, 0, duration);

      // Cubitt: en móvil sin animación de entrada del contenido al cambiar de pestaña (solo el deslizamiento): se veía
      // un salto raro al cambiar de pestaña o pulsar las flechas.
      if (changed && !REDUCED_MOTION.matches && !this.mobileMedia.matches) {
        const targetPanel = this.panels[targetIndex];
        this.transitionTimer = window.setTimeout(() => {
          this.restartPanelAnimation(targetPanel, direction);
        }, Math.min(duration * 0.42, 180));
      }
    }

    updateState(index, options = {}) {
      this.tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
      });

      this.panels.forEach((panel, panelIndex) => {
        const active = panelIndex === index;
        panel.hidden = false;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', active ? 'false' : 'true');
        if ('inert' in panel) panel.inert = !active;
        if (!active) this.stopReviewAutoplay(panel);
      });

      const activePanel = this.panels[index];
      this.ensureActiveReview(activePanel);
      this.startReviewAutoplay(activePanel);
      this.updateArrows();

      if (options.initial) this.restartPanelAnimation(activePanel, null);

      if (options.scrollTab && this.mobileMedia.matches) {
        this.tabs[index].scrollIntoView({
          behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }

    setTrackPosition(index, animate = true, dragOffset = 0, duration = 420) {
      const width = this.getSlideWidth();
      const x = (-index * width) + dragOffset;
      this.track.style.setProperty('--pft-track-duration', `${REDUCED_MOTION.matches ? 0 : duration}ms`);
      this.track.classList.toggle('is-animating', animate && !REDUCED_MOTION.matches);
      if (!animate) this.track.classList.remove('is-animating');
      this.track.style.transform = `translate3d(${x}px, 0, 0)`;
    }

    syncViewportHeight(index, animate = true) {
      const panel = this.panels[index];
      if (!panel) return;
      let height = Math.ceil(panel.scrollHeight || panel.getBoundingClientRect().height);
      // Cubitt: en móvil la altura es la de la pestaña más alta y no se anima. Antes cambiaba en cada pestaña (p. ej.
      // 851 → 932 → 832 px) y las flechas, centradas en el contenedor, saltaban de sitio al cambiar de pestaña.
      if (this.mobileMedia.matches) {
        height = Math.max(height, ...this.panels.map((item) => Math.ceil(item.scrollHeight || 0)));
        animate = false;
      }
      if (!height) return;
      this.viewport.classList.toggle('is-height-animating', animate && !REDUCED_MOTION.matches);
      this.viewport.style.height = `${height}px`;
    }

    updateArrows() {
      if (this.prevButton) {
        const disabled = this.currentIndex <= 0;
        this.prevButton.disabled = disabled;
        this.prevButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      }
      if (this.nextButton) {
        const disabled = this.currentIndex >= this.panels.length - 1;
        this.nextButton.disabled = disabled;
        this.nextButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      }
    }

    restartPanelAnimation(panel, direction = null) {
      if (!panel) return;
      panel.classList.remove('is-entering', 'is-enter-from-left', 'is-enter-from-right');
      if (REDUCED_MOTION.matches) return;
      if (direction === 'next') panel.classList.add('is-enter-from-right');
      if (direction === 'prev') panel.classList.add('is-enter-from-left');
      void panel.offsetWidth;
      requestAnimationFrame(() => {
        panel.classList.add('is-entering');
        window.setTimeout(() => panel.classList.remove('is-entering', 'is-enter-from-left', 'is-enter-from-right'), 1050);
      });
    }

    getReviewSlides(reviews) {
      return Array.from(reviews.querySelectorAll('[data-pft-review-slide]'));
    }

    ensureActiveReview(panel) {
      const reviews = panel.querySelector('[data-pft-reviews]');
      if (!reviews) return;
      const slides = this.getReviewSlides(reviews);
      if (!slides.length) return;
      const active = slides.find((slide) => !slide.hidden) || slides[0];
      this.activateReview(reviews, active.dataset.reviewIndex, false, true);
    }

    activateReview(reviews, index, userInitiated = false, skipAnimation = false) {
      const slides = this.getReviewSlides(reviews);
      const dots = Array.from(reviews.querySelectorAll('[data-pft-review-dot]'));
      if (!slides.length) return;

      slides.forEach((slide) => {
        const active = slide.dataset.reviewIndex === String(index);
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
      });

      dots.forEach((dot) => {
        const active = dot.dataset.reviewIndex === String(index);
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      const activeSlide = slides.find((slide) => slide.dataset.reviewIndex === String(index));
      if (activeSlide && !skipAnimation && !REDUCED_MOTION.matches) {
        activeSlide.classList.remove('is-review-entering');
        void activeSlide.offsetWidth;
        requestAnimationFrame(() => activeSlide.classList.add('is-review-entering'));
      }

      if (userInitiated) {
        const panel = reviews.closest('[data-pft-panel]');
        if (panel && panel.classList.contains('is-active')) {
          this.stopReviewAutoplay(panel);
          this.startReviewAutoplay(panel);
        }
      }
    }

    startReviewAutoplay(panel) {
      const reviews = panel.querySelector('[data-pft-reviews]');
      if (!reviews || !panel.classList.contains('is-active') || reviews.dataset.autoplay !== 'true' || REDUCED_MOTION.matches) return;
      const slides = this.getReviewSlides(reviews);
      if (slides.length < 2) return;

      this.stopReviewAutoplay(panel);
      const delay = Math.max(3000, Number(reviews.dataset.interval || 5000));
      const timer = window.setInterval(() => {
        if (document.hidden || !panel.classList.contains('is-active')) return;
        const activeIndex = Math.max(0, slides.findIndex((slide) => !slide.hidden));
        const next = slides[(activeIndex + 1) % slides.length];
        this.activateReview(reviews, next.dataset.reviewIndex, false);
      }, delay);
      this.reviewTimers.set(panel, timer);
    }

    stopReviewAutoplay(panel) {
      const timer = this.reviewTimers.get(panel);
      if (timer) window.clearInterval(timer);
      this.reviewTimers.delete(panel);
    }
  }

  function initRoot(root) {
    if (!root) return;
    if (root.__pftV5) root.__pftV5.destroy();
    root.__pftV5 = new PremiumFeatureTabsV5(root);
  }

  function initAll(scope = document) {
    if (scope.matches && scope.matches(ROOT_SELECTOR)) initRoot(scope);
    if (scope.querySelectorAll) scope.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  }

  function boot() {
    initAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:reorder', boot);
  document.addEventListener('shopify:block:select', (event) => {
    const blockId = event.detail && event.detail.blockId;
    if (!blockId) return;
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.__pftV5) root.__pftV5.activateByBlockId(blockId);
    });
  });
})();
